import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/pages/Login";
import Captcha from "@/components/Captcha";
import { useHoneypot } from "@/components/Honeypot";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const hp = useHoneypot();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!acceptTerms) {
      toast.error("Please accept the Terms & Privacy Policy to continue");
      return;
    }
    setSubmitting(true);
    try {
      const u = await register(email, password, fullName, captcha, acceptTerms, hp.values());
      toast.success(`Welcome, ${u.full_name.split(" ")[0]}`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      side={
        <>
          <p className="font-editorial italic text-[color:var(--ink-muted)]">
            Access · New members
          </p>
          <h1 className="mt-6 font-editorial text-5xl font-normal leading-[1.05] tracking-tight text-[color:var(--ink)] sm:text-6xl">
            Take the desk.
          </h1>
          <p className="mt-6 max-w-md text-[16px] leading-relaxed text-[color:var(--ink-body)]">
            One account unlocks the unified dashboard — indicators, EAs,
            signals, and courses, bound to the trading account you nominate.
          </p>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid gap-5" data-testid="register-form">
        {hp.field}
        <Field label="Full name">
          <input
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Alex Trader"
            data-testid="register-name"
            className="w-full rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 text-[15px] text-[color:var(--ink)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]"
          />
        </Field>
        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@edge.trade"
            data-testid="register-email"
            className="w-full rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 text-[15px] text-[color:var(--ink)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]"
          />
        </Field>
        <Field label="Password">
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              data-testid="register-password"
              className="w-full rounded-lg border border-[color:var(--ink-hairline)] bg-white px-4 py-3 pr-12 text-[15px] text-[color:var(--ink)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]"
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
              aria-label="Toggle password"
              data-testid="register-toggle-password"
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Field>
        <Captcha onChange={setCaptcha} />
        <label className="flex items-start gap-2.5 text-[13px] text-[color:var(--ink-body)]" data-testid="register-accept-terms-label">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            data-testid="register-accept-terms"
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-[color:var(--ink-hairline)] accent-[color:var(--evergreen)]"
          />
          <span>
            I have read and agree to the{" "}
            <Link to="/legal/terms" target="_blank" className="font-medium text-[color:var(--evergreen)] hover:underline" data-testid="register-terms-link">Terms &amp; Conditions</Link>{" "}
            and{" "}
            <Link to="/legal/privacy" target="_blank" className="font-medium text-[color:var(--evergreen)] hover:underline" data-testid="register-privacy-link">Privacy Policy</Link>.
          </span>
        </label>
        <button
          type="submit"
          disabled={submitting}
          data-testid="register-submit"
          className="btn-edge mt-2 w-full justify-center disabled:cursor-wait disabled:opacity-70"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Create account <ArrowRight size={16} /></>}
        </button>
        <p className="mt-2 text-center text-[14px] text-[color:var(--ink-muted)]">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-[color:var(--evergreen)] hover:underline" data-testid="register-link-login">
            Sign in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-2 block font-editorial italic text-[14px] text-[color:var(--ink-muted)]">
        {label}
      </label>
      {children}
    </div>
  );
}
